import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { pushAd } from './adLoader';
import { adRefreshThrottler } from './AdRefreshThrottler';

export interface AdUnitRef {
  requestRefresh: () => void;
}

interface AdUnitProps {
  slotId: string;
  adSlot: string;
  adFormat?: string;
  width?: number;
  height?: number;
  fullWidthResponsive?: boolean;
  className?: string;
}

const AdUnit = forwardRef<AdUnitRef, AdUnitProps>(
  ({ slotId, adSlot, adFormat, width, height, fullWidthResponsive, className }, ref) => {
    const { currentUser, isPro } = useAuth();
    const [adKey, setAdKey] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    // Push the ad after the <ins> element is in the DOM
    useEffect(() => {
      if (!currentUser || isPro) return;

      const timer = setTimeout(() => {
        pushAd();
        adRefreshThrottler.recordInitialLoad(slotId);
      }, 100);

      return () => clearTimeout(timer);
    }, [adKey, currentUser, isPro, slotId]);

    // Clean up throttler on unmount
    useEffect(() => {
      return () => {
        adRefreshThrottler.clearSlot(slotId);
      };
    }, [slotId]);

    const doRefresh = useCallback(() => {
      setAdKey(k => k + 1);
    }, []);

    useImperativeHandle(ref, () => ({
      requestRefresh: () => {
        adRefreshThrottler.requestRefresh(slotId, doRefresh);
      }
    }), [slotId, doRefresh]);

    // Don't render anything for Pro users or unauthenticated users
    if (!currentUser || isPro) return null;

    const style: React.CSSProperties = {
      display: 'block',
      ...(width ? { width } : {}),
      ...(height ? { height } : {}),
    };

    return (
      <div ref={containerRef} className={className}>
        <ins
          key={adKey}
          className="adsbygoogle"
          style={style}
          data-ad-client="ca-pub-3460213543571267"
          data-ad-slot={adSlot}
          {...(adFormat ? { 'data-ad-format': adFormat } : {})}
          {...(fullWidthResponsive ? { 'data-full-width-responsive': 'true' } : {})}
        />
      </div>
    );
  }
);

AdUnit.displayName = 'AdUnit';

export default AdUnit;
